import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { Air, Dashboard, Functions, Group, Logout, Memory, PlayCircle, RocketLaunch, SettingsEthernet } from '@mui/icons-material';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import KeyIcon from '@mui/icons-material/Key';
import { Box, CSSObject, Divider, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, styled, SxProps, Theme, Typography } from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';

import { useKeycloak } from '../../KeycloakProvider';
import CustomTooltip from '../common/CustomTooltip';


const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
});

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    variants: [
        {
            props: ({ open }) => open,
            style: {
                ...openedMixin(theme),
                '& .MuiDrawer-paper': openedMixin(theme),
            },
        },
        {
            props: ({ open }) => !open,
            style: {
                ...closedMixin(theme),
                '& .MuiDrawer-paper': closedMixin(theme),
            },
        },
    ],
}));

const getItemButtonCss = (state: boolean): SxProps<Theme> => {
    return {
        minHeight: 48,
        px: 2.5,
        justifyContent: state ? 'initial' : 'center',
    }
}

const getItemIconCss = (state: boolean): SxProps<Theme> => {
    return {
        minWidth: 0,
        justifyContent: 'center',
        mr: state ? 3 : 'auto',
    }
}

interface Navigation {
    name: string,
    icon: JSX.Element,
    linkTo: string
}

interface SidebarProps {
    showManageUsers: boolean
}

const Sidebar: React.FC<SidebarProps> = ({ showManageUsers }) => {

    const navigations: Navigation[] = [
        // {
        //     name: 'Overview',
        //     icon: <Dashboard />,
        //     linkTo: '/'
        // },
        // {
        //     name: 'Monitoring',
        //     icon: <Analytics />,
        //     linkTo: '/monitoring'
        // },
        {
            name: 'Dashboard',
            icon: <Dashboard />,
            linkTo: '/'
        },
        {
            name: 'Executions',
            icon: <PlayCircle />,
            linkTo: '/executions'
        },
        {
            name: 'Flows',
            icon: <Air />,
            linkTo: '/flows'
        },
        {
            name: 'Blocks',
            icon: <Functions />,
            linkTo: '/blocks'
        },
        // {
        //     name: 'Model Hub',
        //     icon: <Hub />,
        //     linkTo: '/models'
        // },


        // {
        //     name: 'Logs',
        //     icon: <Assignment />,
        //     linkTo: '/logs'
        // },
        {
            name: 'Deployments',
            icon: <RocketLaunch />,
            linkTo: '/deployments'
        },
        {
            name: 'Namespaces',
            icon: <SettingsEthernet />,
            linkTo: '/namespaces'
        },
        {
            name: 'Secrets & Variables',
            icon: <KeyIcon />,
            linkTo: '/secrets-and-variables'
        },
        // <Route path="secrets_and_variables" element={<SecretsAndVariables />} />
        ...(showManageUsers ? [{ name: 'Users', icon: <Group />, linkTo: '/manage_users' }] : []),
    ]

    const location = useLocation();
    // const navigate = useNavigate();
    const { logout } = useKeycloak();

    const [open, setOpen] = useState<boolean>(false);
    const [selectedNav, setselectedNav] = useState<string>();

    const handleToggle = () => {
        setOpen(!open);
    };

    const handleLogout = () => {
        // navigate('/executions');
        logout();
    }

    useEffect(() => {
        setselectedNav(location.pathname.split("/")[1])

        return () => {
        }
    }, [location.pathname])

    return (
        <Drawer variant="permanent" open={open}>
            <List disablePadding>
                <ListItem sx={{ height: "50px" }}>
                    <ListItemIcon sx={{ minWidth: open ? '56px' : 'auto' }}>
                        <Memory />
                    </ListItemIcon>

                    {open &&
                        <Typography variant="h6" component="div">
                            FlowX
                        </Typography>
                    }
                </ListItem>

                <Divider />

                {navigations.map((item) => (
                    <ListItem key={item.name} disablePadding sx={{ color: 'inherit' }} component={Link} to={item.linkTo}>
                        <ListItemButton
                            selected={selectedNav === item.linkTo.split("/")[1]}
                            sx={{ ...getItemButtonCss(open) }}
                        >
                            <CustomTooltip title={open ? "" : item.name} placement='right'>
                                <ListItemIcon sx={{ ...getItemIconCss(open) }}>
                                    {item.icon}
                                </ListItemIcon>
                            </CustomTooltip>

                            <ListItemText primary={item.name} sx={{ opacity: open ? 1 : 0 }} />
                        </ListItemButton>
                    </ListItem>
                ))}

                <ListItem disablePadding sx={{ color: 'inherit' }} >
                    <ListItemButton
                        onClick={handleLogout}
                        sx={{ ...getItemButtonCss(open) }}
                    >
                        <CustomTooltip title={open ? "" : 'Logout'} placement='right'>
                            <ListItemIcon sx={{ ...getItemIconCss(open) }}>
                                <Logout />
                            </ListItemIcon>
                        </CustomTooltip>

                        <ListItemText primary='Logout' sx={{ opacity: open ? 1 : 0 }} />
                    </ListItemButton>
                </ListItem>
            </List>

            <Box sx={{ flexGrow: 1 }} />

            <Divider />

            <ListItem sx={{ justifyContent: open ? 'flex-end' : 'center', height: "50px" }}>
                <IconButton onClick={handleToggle}>
                    {open ? <ChevronLeft /> : <ChevronRight />}
                </IconButton>
            </ListItem>
        </Drawer >
    );
}

export default Sidebar